import { db } from '../firebase';
import { collection, query, where, orderBy, limit, getDocs, Timestamp } from 'firebase/firestore';
import { PetConversationSummary } from '../types/recentMemory';
import { PetProfile } from '../types/onboarding';
import { petConfirmationService, PetConfirmationRequest, PetConfirmationResult } from './petConfirmationService';

/**
 * Roadmap Context Service
 * 
 * Enhances roadmap generation by providing rich context from:
 * - Pet profiles (from settings)
 * - Recent conversation summaries (from memory system)
 * - Pet-specific conversation history
 */
export class RoadmapContextService {
  private db = db;

  /**
   * Check if pet confirmation is needed before proceeding
   */
  async checkPetConfirmationNeeded(
    userId: string,
    userQuestion: string,
    userLanguage: 'en' | 'zh-CN' | 'es' | 'fr' = 'zh-CN'
  ): Promise<PetConfirmationResult> {
    try {
      console.log('🔍 Checking if pet confirmation is needed...');
      
      // 1. Extract pet name from question
      const petName = this.extractPetNameFromQuestion(userQuestion);
      console.log('🐾 Extracted pet name:', petName);
      
      if (!petName) {
        return {
          needsConfirmation: false,
          confirmationMessage: '',
          shouldProceed: true
        };
      }
      
      // 2. Get pet profiles
      const petProfiles = await this.getPetProfiles(userId);
      console.log('📋 Available pet profiles:', petProfiles.map(p => ({ name: p.name, type: p.type })));
      
      // 3. Find matching pets
      const matchingPets = petProfiles.filter(pet => pet.name === petName);
      console.log('🎯 Matching pets found:', matchingPets.length);
      
      // 4. Check if confirmation is needed
      const confirmationRequest: PetConfirmationRequest = {
        detectedPetName: petName,
        matchingPets,
        userQuestion,
        userLanguage
      };
      
      const result = petConfirmationService.checkPetConfirmation(confirmationRequest);
      console.log('✅ Pet confirmation check result:', result);
      
      return result;
      
    } catch (error) {
      console.error('❌ Error checking pet confirmation:', error);
      return {
        needsConfirmation: false,
        confirmationMessage: '',
        shouldProceed: true
      };
    }
  }

  /**
   * Get enhanced context for roadmap generation
   */
  async getRoadmapContext(
    userId: string, 
    userQuestion: string, 
    conversationContext: string
  ): Promise<EnhancedRoadmapContext> {
    try {
      console.log('🔍 Getting enhanced roadmap context for user:', userId);

      // 1. Extract pet name from question (like "汤圆")
      const petName = this.extractPetNameFromQuestion(userQuestion);
      console.log('🐾 Extracted pet name:', petName);

      // 2. Get pet profiles from settings
      const petProfiles = await this.getPetProfiles(userId);
      console.log('📋 Pet profiles found:', petProfiles.length);

      // 3. Get recent conversation summaries for the specific pet
      const recentSummaries = petName ? 
        await this.getRecentSummariesForPet(userId, petName) : 
        await this.getRecentSummariesForUser(userId);
      console.log('🧠 Recent summaries found:', recentSummaries.length);

      // 4. Find the specific pet profile if pet name was detected
      console.log('🔍 Pet name matching:', {
        extractedPetName: petName,
        availablePetNames: petProfiles.map(p => p.name),
        petProfiles: petProfiles.map(p => ({ name: p.name, type: p.type }))
      });
      
      let specificPetProfile: PetProfile | undefined;
      
      if (petName) {
        // Try exact match first
        specificPetProfile = petProfiles.find(pet => pet.name === petName);
        
        // If no exact match, try partial match
        if (!specificPetProfile) {
          specificPetProfile = petProfiles.find(pet => 
            pet.name.includes(petName) || petName.includes(pet.name)
          );
        }
        
        console.log('🔍 Pet matching result:', {
          petName,
          exactMatch: petProfiles.find(pet => pet.name === petName),
          partialMatch: petProfiles.find(pet => 
            pet.name.includes(petName) || petName.includes(pet.name)
          ),
          selected: specificPetProfile
        });
      } else {
        // Use first pet if no specific name found
        specificPetProfile = petProfiles[0];
      }
        
      console.log('🎯 Selected pet profile:', specificPetProfile ? {
        name: specificPetProfile.name,
        type: specificPetProfile.type
      } : 'No matching pet found');

      // 5. Build enhanced context
      const enhancedContext = this.buildEnhancedContext(
        userQuestion,
        conversationContext,
        petProfiles,
        recentSummaries,
        specificPetProfile,
        petName
      );

      console.log('✅ Enhanced context built successfully');
      return enhancedContext;

    } catch (error) {
      console.error('❌ Error getting roadmap context:', error);
      // Return basic context as fallback
      return this.buildBasicContext(userQuestion, conversationContext);
    }
  }

  /**
   * Extract pet name from user question using common patterns
   */
  private extractPetNameFromQuestion(question: string): string | null {
    // More specific patterns to avoid extracting action words
    const patterns = [
      // Chinese patterns: "了解朵朵的饮食喜好", "汤圆的饮食", "创建一个了解朵朵饮食喜好的7天个性化计划"
      /了解(.+?)的饮食喜好/,     // 了解朵朵的饮食喜好
      /了解(.+?)的饮食/,        // 了解朵朵的饮食
      /了解(.+?)的喜好/,        // 了解朵朵的喜好
      /(.+?)的饮食喜好/,        // 朵朵的饮食喜好
      /(.+?)的饮食/,           // 朵朵的饮食
      /(.+?)的喜好/,           // 朵朵的喜好
      /(.+?)的/,              // 朵朵的
      /(.+?)饮食喜好/,         // 朵朵饮食喜好
      /(.+?)饮食/,            // 朵朵饮食
      /(.+?)喜好/,            // 朵朵喜好
      // More specific patterns for complex sentences
      /创建.*?了解(.+?)饮食喜好/, // 创建一个了解朵朵饮食喜好的7天个性化计划
      /创建.*?了解(.+?)饮食/,    // 创建一个了解朵朵饮食的7天个性化计划
      /创建.*?了解(.+?)喜好/,    // 创建一个了解朵朵喜好的7天个性化计划
      // Conversation history patterns: "关于朵朵的话题", "聊了哪些关于朵朵的话题"
      /关于(.+?)的话题/,        // 关于朵朵的话题
      /聊了哪些关于(.+?)的话题/, // 聊了哪些关于朵朵的话题
      /最近我们聊了哪些关于(.+?)的话题/, // 最近我们聊了哪些关于朵朵的话题
      /(.+?)的话题/,           // 朵朵的话题
      /关于(.+?)的/,           // 关于朵朵的
      // English patterns: "my dog", "my cat", "Fluffy", "Buddy"
      /my\s+(dog|cat|pet)\s+(\w+)/gi,
      /(?:dog|cat|pet)\s+(\w+)/gi,
      // Spanish patterns: "mi perro", "mi gato"
      /mi\s+(perro|gato|mascota)\s+(\w+)/gi,
      // French patterns: "mon chien", "mon chat"
      /mon\s+(chien|chat|animal)\s+(\w+)/gi
    ];

    for (const pattern of patterns) {
      const matches = question.match(pattern);
      if (matches && matches.length > 0) {
        // For Chinese patterns, extract the captured group
        if (pattern.source.includes('(.+?)')) {
          const match = matches[1]; // Get the captured group
          if (match && match.length > 0) {
            // Additional validation for Chinese pet names
            // Should be 1-4 characters and not contain action words
            const actionWords = ['创建', '一个', '了解', '的', '饮食', '喜好', '计划', '天', '个性化', '话题', '聊了', '哪些', '关于', '最近', '我们', '告诉', '你', '我', '能', '吗', '？', '?'];
            const isActionWord = actionWords.some(word => match.includes(word));
            
            // More lenient validation for conversation history patterns
            const isConversationHistoryPattern = pattern.source.includes('话题') || pattern.source.includes('关于');
            const maxLength = isConversationHistoryPattern ? 6 : 4; // Allow longer names for conversation patterns
            
            if (!isActionWord && match.length <= maxLength && match.length >= 1) {
              console.log('🐾 Extracted pet name from pattern:', pattern.source, '->', match);
              return match.trim();
            }
          }
        } else {
          // For other patterns, use the full match
          const match = matches[0];
          // Clean up common words
          const cleaned = match.replace(/(my|mi|mon)\s+(dog|cat|pet|perro|gato|mascota|chien|chat|animal)\s+/gi, '').trim();
          if (cleaned.length > 1) {
            console.log('🐾 Extracted pet name from pattern:', pattern.source, '->', cleaned);
            return cleaned;
          }
        }
      }
    }

    return null;
  }

  /**
   * Get pet profiles from user settings
   */
  private async getPetProfiles(userId: string): Promise<PetProfile[]> {
    try {
      const petProfilesRef = collection(db, 'users', userId, 'petProfiles');
      const petQuery = query(petProfilesRef, orderBy('createdAt', 'desc'));
      const petSnapshot = await getDocs(petQuery);
      
      const pets: PetProfile[] = [];
      petSnapshot.forEach((doc) => {
        pets.push({ id: doc.id, ...doc.data() } as PetProfile);
      });
      
      return pets;
    } catch (error) {
      console.error('Error fetching pet profiles:', error);
      return [];
    }
  }

  /**
   * Get recent conversation summaries for a specific pet
   */
  private async getRecentSummariesForPet(userId: string, petName: string): Promise<PetConversationSummary[]> {
    try {
      // Get summaries from the last 3 months (90 days)
      const threeMonthsAgo = new Date();
      threeMonthsAgo.setDate(threeMonthsAgo.getDate() - 90);
      const threeMonthsAgoTimestamp = Timestamp.fromDate(threeMonthsAgo);
      
      const summariesRef = collection(db, 'petConversationSummaries');
      const summariesQuery = query(
        summariesRef,
        where('userId', '==', userId),
        where('petName', '==', petName),
        where('lastUpdated', '>=', threeMonthsAgoTimestamp),
        orderBy('lastUpdated', 'desc'),
        limit(20) // Increased limit to get more comprehensive history
      );
      
      const summariesSnapshot = await getDocs(summariesQuery);
      const summaries: PetConversationSummary[] = [];
      
      summariesSnapshot.forEach((doc) => {
        summaries.push({ id: doc.id, ...doc.data() } as PetConversationSummary);
      });
      
      console.log(`🧠 Retrieved ${summaries.length} conversation summaries for ${petName} from last 3 months`);
      return summaries;
    } catch (error) {
      console.error('Error fetching pet summaries:', error);
      return [];
    }
  }

  /**
   * Get recent conversation summaries for user (all pets)
   */
  private async getRecentSummariesForUser(userId: string): Promise<PetConversationSummary[]> {
    try {
      // Get summaries from the last 3 months (90 days)
      const threeMonthsAgo = new Date();
      threeMonthsAgo.setDate(threeMonthsAgo.getDate() - 90);
      const threeMonthsAgoTimestamp = Timestamp.fromDate(threeMonthsAgo);
      
      const summariesRef = collection(db, 'petConversationSummaries');
      const summariesQuery = query(
        summariesRef,
        where('userId', '==', userId),
        where('lastUpdated', '>=', threeMonthsAgoTimestamp),
        orderBy('lastUpdated', 'desc'),
        limit(20) // Increased limit to get more comprehensive history
      );
      
      const summariesSnapshot = await getDocs(summariesQuery);
      const summaries: PetConversationSummary[] = [];
      
      summariesSnapshot.forEach((doc) => {
        summaries.push({ id: doc.id, ...doc.data() } as PetConversationSummary);
      });
      
      console.log(`🧠 Retrieved ${summaries.length} conversation summaries for user from last 3 months`);
      return summaries;
    } catch (error) {
      console.error('Error fetching user summaries:', error);
      return [];
    }
  }

  /**
   * Build enhanced context with pet profiles and conversation history
   */
  private buildEnhancedContext(
    userQuestion: string,
    conversationContext: string,
    petProfiles: PetProfile[],
    recentSummaries: PetConversationSummary[],
    specificPetProfile: PetProfile | undefined,
    detectedPetName: string | null
  ): EnhancedRoadmapContext {
    // Build pet context string
    let petContextString = '';
    
    if (specificPetProfile) {
      petContextString = this.buildPetProfileContext(specificPetProfile);
    } else if (petProfiles.length > 0) {
      petContextString = this.buildPetProfileContext(petProfiles[0]);
    }

    // Build conversation history context
    const conversationHistoryContext = this.buildConversationHistoryContext(recentSummaries);

    // Build enhanced conversation context - prioritize both conversation history AND pet profiles
    const enhancedConversationContext = [
      conversationContext,
      conversationHistoryContext, // Recent conversation summaries
      petContextString // Pet profile information
    ].filter(Boolean).join('\n\n');

    return {
      userQuestion,
      conversationContext: enhancedConversationContext,
      petProfiles,
      recentSummaries,
      specificPetProfile,
      detectedPetName,
      petContextString,
      conversationHistoryContext,
      hasPetContext: !!specificPetProfile || petProfiles.length > 0,
      hasConversationHistory: recentSummaries.length > 0
    };
  }

  /**
   * Build pet profile context string
   */
  private buildPetProfileContext(petProfile: PetProfile): string {
    const contextParts = [];
    
    contextParts.push(`PET PROFILE INFORMATION:`);
    contextParts.push(`Name: ${petProfile.name}`);
    contextParts.push(`Type: ${petProfile.type}`);
    
    if (petProfile.breed) {
      contextParts.push(`Breed: ${petProfile.breed}`);
    }
    
    if (petProfile.age) {
      contextParts.push(`Age: ${petProfile.age} years old`);
    }
    
    if (petProfile.gender) {
      contextParts.push(`Gender: ${petProfile.gender}`);
    }
    
    if (petProfile.temperament) {
      contextParts.push(`Temperament: ${petProfile.temperament}`);
    }

    if (petProfile.specialNeeds) {
      contextParts.push(`Special Needs: ${petProfile.specialNeeds}`);
    }

    if (petProfile.healthConditions) {
      contextParts.push(`Health Conditions: ${petProfile.healthConditions}`);
    }

    if (petProfile.behavioralIssues) {
      contextParts.push(`Behavioral Issues: ${petProfile.behavioralIssues}`);
    }

    if (petProfile.dietType) {
      contextParts.push(`Diet Type: ${petProfile.dietType}`);
    }

    if (petProfile.medications) {
      contextParts.push(`Medications: ${petProfile.medications}`);
    }
    
    if (petProfile.activityLevel) {
      contextParts.push(`Activity Level: ${petProfile.activityLevel}`);
    }
    
    if (petProfile.trainingLevel) {
      contextParts.push(`Training Level: ${petProfile.trainingLevel}`);
    }
    
    if (petProfile.weight) {
      contextParts.push(`Weight: ${petProfile.weight.value} ${petProfile.weight.unit}`);
    }

    return contextParts.join('\n');
  }

  /**
   * Build conversation history context from recent summaries
   */
  private buildConversationHistoryContext(summaries: PetConversationSummary[]): string {
    if (summaries.length === 0) {
      return '';
    }

    const contextParts = ['RECENT CONVERSATION HISTORY (Last 3 Months):'];
    
    // Show more summaries (up to 10) for comprehensive history
    summaries.slice(0, 10).forEach((summary, index) => {
      contextParts.push(`\n${index + 1}. Conversation about ${summary.petName} (${summary.lastUpdated.toDate().toLocaleDateString()}):`);
      contextParts.push(`   Summary: ${summary.summary}`);
      
      if (summary.keyTopics.length > 0) {
        contextParts.push(`   Key Topics Discussed: ${summary.keyTopics.join(', ')}`);
      }
      
      if (summary.petCareAdvice.length > 0) {
        contextParts.push(`   Pet Care Advice Provided: ${summary.petCareAdvice.join('; ')}`);
      }
      
      if (summary.trainingProgress && summary.trainingProgress.length > 0) {
        contextParts.push(`   Training Progress & Challenges: ${summary.trainingProgress.join(', ')}`);
      }
      
      if (summary.healthNotes && summary.healthNotes.length > 0) {
        contextParts.push(`   Health Observations: ${summary.healthNotes.join(', ')}`);
      }
      
      if (summary.behaviorObservations && summary.behaviorObservations.length > 0) {
        contextParts.push(`   Behavior Observations: ${summary.behaviorObservations.join(', ')}`);
      }
    });

    return contextParts.join('\n');
  }

  /**
   * Build basic context as fallback
   */
  private buildBasicContext(userQuestion: string, conversationContext: string): EnhancedRoadmapContext {
    return {
      userQuestion,
      conversationContext,
      petProfiles: [],
      recentSummaries: [],
      specificPetProfile: undefined,
      detectedPetName: null,
      petContextString: '',
      conversationHistoryContext: '',
      hasPetContext: false,
      hasConversationHistory: false
    };
  }
}

/**
 * Enhanced roadmap context interface
 */
export interface EnhancedRoadmapContext {
  userQuestion: string;
  conversationContext: string;
  petProfiles: PetProfile[];
  recentSummaries: PetConversationSummary[];
  specificPetProfile?: PetProfile;
  detectedPetName: string | null;
  petContextString: string;
  conversationHistoryContext: string;
  hasPetContext: boolean;
  hasConversationHistory: boolean;
}

// Export singleton instance
export const roadmapContextService = new RoadmapContextService();
